import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { Destination, Form, FormField, FormNode, getDefaultBucketVendor, getDefaultMetastore } from "@prequel/react";
import { Button, ButtonStyle, Spinner } from "@prequel/shared-components";

import ConfirmDataDestinationModal from "../../../components/ConfirmDataDestinationModal";
import EgressIPsInstructions from "../../../components/EgressIPsInstructions";
import AdvancedOptions from "./AdvancedOptions";

import { useCreateMagicLink, useMagicLinkForm } from "@/api/queries/magic-links";
import { useAllRecipients } from "@/api/queries/recipients";
import WrapperSelector from "@/components/WrapperSelector";
import { MagicLink } from "@/types";

const VENDOR_SECTION_ID = 1;
const CREDENTIALS_SECTION_ID = 3;
const ADVANCED_OPTIONS_SECTION_ID = 6;

const getDefaultMagicLink = (): MagicLink => ({
  name: "",
  recipient_id: "",
  vendor: "snowflake",
  set_destination_enabled: true,
  redirect_url: "",
});

const useMagicLink: (
  initialState?: Partial<MagicLink>,
) => [MagicLink, React.Dispatch<React.SetStateAction<Partial<MagicLink>>>] = (initialState) => {
  const [magicLink, setMagicLink] = useState<MagicLink>({
    ...getDefaultMagicLink(),
    ...initialState,
  });

  const setPartialMagicLink = (update: Partial<MagicLink> | ((prevState: MagicLink) => Partial<MagicLink>)) => {
    setMagicLink((prevState) => ({
      ...prevState,
      ...(typeof update === "function" ? update(prevState) : update),
    }));
  };

  return [magicLink, setPartialMagicLink];
};

// Recursively traverses and flattens tree of children
const getFormFields: (parentPath: string[], node: FormNode | null, magicLink: MagicLink) => FormField[] = (
  parentPath,
  node,
  magicLink,
) => {
  if (!node) {
    return [];
  }

  const currentPath = parentPath.concat([node.name]);
  const result: FormField[] = [buildFormField(node, currentPath)];

  for (const child of node.children || []) {
    if (magicLink[node.name] === child.parent) {
      result.push(...getFormFields(parentPath, child, magicLink));
    }
  }

  return result;
};

const buildFormField: (node: FormNode, path: string[]) => FormField = (node, path) => {
  const baseResult = {
    prepared_destination_path: deduplicate(path).join("."),
    name: node.name as keyof Destination,
    required: node.required,
    type: node.type,
    label: node.attributes.label,
    placeholder: node.attributes.placeholder,
    description: node.attributes.description,
    internal: node.attributes.internal,
    const: node.const,
    section_id: node.attributes.section_id,
    input_type: node.attributes.input_type,
  };

  if (node.attributes.form_element === "input") {
    return {
      ...baseResult,
      form_element: "input",
      input_type: node.attributes.input_type,
    } as FormField;
  } else if (node.attributes.form_element === "textarea") {
    return {
      ...baseResult,
      form_element: "textarea",
    } as FormField;
  } else if (node.attributes.form_element === "radio") {
    return {
      ...baseResult,
      form_element: "radio",
      enum: node.enum,
    } as FormField;
  } else if (node.attributes.form_element === "select") {
    return {
      ...baseResult,
      form_element: "select",
      enum: node.enum,
    } as FormField;
  } else if (node.attributes.form_element === "div") {
    return {
      ...baseResult,
      form_element: "div",
    } as FormField;
  }

  return {
    ...baseResult,
    form_element: "div",
  } as FormField;
};

const deduplicate = (items: string[]): string[] => {
  return Array.from(new Set(items));
};

// The magic link API doesn't represent fields that are conditional based on anything other than vendor
// so we need to filter out some fields
const conditionalFieldRules: Record<string, (magicLink: MagicLink) => boolean> = {
  bucket_access_id: (ml) => {
    if (ml.vendor === "iceberg") {
      return ml.catalog === "catalog_iceberg_rest" && ml.bucket_vendor === "abs";
    }
    return ml.bucket_vendor === "abs" || ml.vendor === "abs";
  },
  bucket_name: (ml) => ml.vendor !== "databricks" || ml.metastore === "hive",
  host: (ml) => {
    if (ml.vendor === "iceberg") {
      return ml.catalog === "catalog_iceberg_rest";
    }
    if (ml.vendor === "delta_lake") {
      return ml.bucket_vendor === "s3_compatible";
    }
    return true;
  },
  table_bucket_arn: (ml) => ml.vendor === "iceberg" && ml.catalog === "catalog_s3_tables",
  bucket_host: (ml) =>
    ml.vendor === "iceberg" && ml.catalog === "catalog_iceberg_rest" && ml.bucket_vendor === "s3_compatible",
  bucket_vendor: (ml) => {
    if (ml.vendor === "databricks") {
      return ml.metastore === "hive";
    }
    return ml.vendor !== "iceberg" || ml.catalog === "catalog_iceberg_rest";
  },
};

const filterConditionalFields = (fields: FormField[], magicLink: MagicLink): FormField[] => {
  return fields.filter((field) => {
    const rule = conditionalFieldRules[field.name];
    // If there’s a rule, apply it; if not, keep the field
    return rule ? rule(magicLink) : true;
  });
};

const MagicLinkForm = () => {
  const { data: recipients = [] } = useAllRecipients();
  const { data: magicLinkForm } = useMagicLinkForm();
  const createMagicLinkMutation = useCreateMagicLink();
  const formRef = useRef<HTMLFormElement>(null);

  const [showModal, setShowModal] = useState(false);
  const [magicLink, setMagicLink] = useMagicLink();

  const form: Form | undefined = useMemo(() => {
    if (magicLinkForm?.form) {
      const { form, sections } = magicLinkForm;
      const newFields = getFormFields([], form, magicLink);
      const filteredFields = filterConditionalFields(newFields, magicLink);

      const newSections = sections.map((section) => ({
        ...section,
        fields: filteredFields
          .filter(({ section_id }) => section_id === section.id)
          .filter((field) => {
            if (field.name === "vendor" && section.id !== VENDOR_SECTION_ID) {
              return false;
            }
            return true;
          }),
      }));

      const filteredSections = newSections.filter(({ fields }) => fields.length > 0);

      return filteredSections;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magicLinkForm, magicLink.vendor, magicLink.bucket_vendor, magicLink.metastore, magicLink.catalog]);

  const selectedVendor = useMemo(() => {
    if (form) {
      const { fields } = form[0];
      if (fields[0].form_element === "select") {
        return fields[0].enum?.find(({ key }) => key === magicLink.vendor);
      }
    }
    return undefined;
  }, [form, magicLink.vendor]);

  const isProcessing = createMagicLinkMutation.isPending;

  const selectedRecipientObject = useMemo(
    () => recipients?.find(({ id }) => id === magicLink.recipient_id),
    [magicLink.recipient_id, recipients],
  );

  useEffect(() => {
    if (recipients && recipients.length > 0 && !magicLink.recipient_id) {
      setMagicLink({ recipient_id: recipients[0].id });
    }
  }, [recipients, magicLink.recipient_id, setMagicLink]);

  useEffect(() => {
    // when the vendor changes, set defaults and unset fields that are not displayed for the new vendor
    if (selectedVendor && form) {
      const vendor = selectedVendor.key.toString();
      const defaultBucketVendor = getDefaultBucketVendor(vendor);
      const defaultMetastore = getDefaultMetastore(vendor);

      const displayedFields = form.flatMap((section) => section.fields.map((field) => field.name));
      const fieldsToUnset: Partial<MagicLink> = {};
      Object.keys(magicLink).forEach((key) => {
        if (!displayedFields.includes(key as keyof Destination)) {
          fieldsToUnset[key as keyof MagicLink] = undefined;
        }
      });

      setMagicLink({
        bucket_vendor: defaultBucketVendor,
        metastore: defaultMetastore,
        ...fieldsToUnset,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendor]);

  const confirm = () => {
    setShowModal(false);
    createMagicLinkMutation.mutate({
      link: magicLink,
      redirectPath: "/magic-links/active",
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowModal(true);
  };

  if (form === undefined) {
    return <Spinner />;
  }

  return (
    <>
      <div className="h-full">
        <form className="space-y-8" onSubmit={handleSubmit} ref={formRef}>
          {form.map((section) => {
            if (section.id === ADVANCED_OPTIONS_SECTION_ID) {
              return (
                <Fragment key={section.id}>
                  <div>
                    <AdvancedOptions
                      link={magicLink}
                      setLinkField={(key, value) => setMagicLink({ [key]: value })}
                      disabled={isProcessing}
                    />
                  </div>
                </Fragment>
              );
            }

            return (
              <Fragment key={section.id}>
                <div key={section.id}>
                  {section.id !== VENDOR_SECTION_ID && (
                    <>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        {section.title}
                      </label>
                      {section.subtitle ? (
                        <div className="mt-1 mb-4">
                          <div className="mt-1 text-sm text-gray-500">
                            {section.subtitle}
                            {section.id === CREDENTIALS_SECTION_ID && selectedVendor && (
                              <>
                                {" For assistance, "}
                                <a
                                  href={selectedVendor.docs}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="
                                    font-medium text-primary-600
                                    hover:text-primary-500
                                  "
                                >
                                  view our documentation on {selectedVendor.display}.
                                </a>
                                <EgressIPsInstructions />
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        <br />
                      )}
                    </>
                  )}
                  <div className="space-y-4">
                    {section.fields.map((field) => (
                      <div key={field.name}>
                        {
                          <WrapperSelector
                            field={field}
                            connection={magicLink}
                            setConnectionField={(key, value) => setMagicLink({ [key]: value })}
                            disabled={isProcessing}
                            isEditing={false}
                          />
                        }
                      </div>
                    ))}
                  </div>
                </div>
                <div className="h-px w-full bg-gray-200" /> {/* Divider  */}
              </Fragment>
            );
          })}
        </form>
        <div className="mt-8 flex justify-end">
          <Button
            type={ButtonStyle.PRIMARY}
            disabled={isProcessing}
            onClick={() => formRef.current?.requestSubmit()}
            text="Generate Link"
          />
        </div>
      </div>
      <ConfirmDataDestinationModal
        kind="GENERATE_LINK"
        show={showModal}
        setShow={setShowModal}
        dataIdentifier={selectedRecipientObject ? selectedRecipientObject.name : ""}
        dataDestination={magicLink}
        onConfirm={confirm}
      />
    </>
  );
};

export default MagicLinkForm;
